import {
    Box,
    Dialog,
    DialogProps,
    InternalStandardProps,
    Slide,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useEffect } from "react";
import { TransitionProps } from "@mui/material/transitions";
import { useNavigate } from "react-router-dom";
import { useConsentForm, useGetConsentText } from "JS/React/Hooks/ConsentForm";
import { AppHeader } from "JS/React/Components/AppHeader";
import { messaging } from "JS/Helpers/UserMessaging";
import { AppTypography } from "JS/React/Components/AppTypography";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { AppButton } from "JS/React/Components/AppButton";
import { useFirebaseLogger } from "JS/React/Hooks/Firebase";
import { EventActions, EventNames } from "JS/Models";
import { useAppDispatch } from "JS/Redux/Store";
import {
    resetRedirectionState,
    setLoadingState,
    setRedirectionState,
} from "JS/Redux/ConsentForm";
import { config } from "JS/Config";
import { ConsentStatusService } from "JS/Services/ConsentForm/ConsentStatusService";
import { captureSentryError } from "JS/Helpers/SentryHelper";
import { ConsentFormDexie } from "JS/Database/Dexie";
import { setConsentStatusById } from "JS/Database/ConsentForm";
import { setIsLtdConsentFormOpened } from "JS/Helpers/LocalStorageHelpers";

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

interface ConsentFormProps extends InternalStandardProps<Omit<DialogProps, "open">> {}

const StyledRoot = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.default,
    height: "100vh",
}));

const StyledText = styled(AppTypography)(({ theme }) => ({
    color: theme.palette.grey[600],
    padding: "0 15px",
}));

const StyledTextBox = styled(Box)(({ theme }) => ({
    overflow: "auto",
    marginTop: "15px",
    border: `1px solid ${theme.palette.grey[600]}`,
    margin: "0 15px",
    color: theme.palette.grey[600],
    padding: "0 15px",
}));

const StyledButtonHolder = styled(Box)({
    position: "absolute",
    bottom: "20",
    width: "100%",
    display: "flex",
});

const StyledButton = styled(AppButton)({
    width: "50%",
    margin: "10px",
});

const StyledDismissButton = styled(StyledButton)(({ theme }) => ({
    background: theme.palette.grey[200],
    color: "black",
    "&:hover": {
        background: theme.palette.grey[400],
        color: "black",
    },
}));

export const ConsentForm = (props: ConsentFormProps) => {
    const { header, subHeader, text, loading, consentGiven, dismiss } =
        useHandlers();
    return (
        <Dialog
            scroll="body"
            fullScreen
            open={true}
            TransitionComponent={Transition}
        >
            {loading && (
                <AppBackdropProgress
                    backdropText={messaging.loader.loadingSession}
                    open={true}
                />
            )}
            <StyledRoot>
                <AppHeader
                    title={header}
                    onBackClick={dismiss}
                    canGoBack={true}
                    searchable={false}
                />
                <StyledText>{subHeader}</StyledText>
                <StyledTextBox height={"70vh"}>
                    <AppTypography>{text}</AppTypography>
                </StyledTextBox>
                <StyledButtonHolder>
                    <StyledButton
                        variant="contained"
                        buttonVariant="primary-filled"
                        onClick={consentGiven}
                    >
                        I Agree
                    </StyledButton>
                    <StyledDismissButton
                        variant="contained"
                        onClick={dismiss}
                    >
                        Dismiss
                    </StyledDismissButton>
                </StyledButtonHolder>
            </StyledRoot>
        </Dialog>
    );
};

const useHandlers = () => {
    const { loading: postLoading, success, consentGiven } = useConsentForm();
    const {
        header,
        subHeader,
        text,
        loading: textLoading,
    } = useGetConsentText();
    const navigate = useNavigate();
    const { logFirebaseEvent } = useFirebaseLogger();
    const dispatch = useAppDispatch();
    const dismiss = () => {
        logFirebaseEvent(EventNames.CONSENT_FORM_DISMISSED, {
            action: EventActions.CONSENT_FORM,
        });

        const service = new ConsentStatusService(config.user.memberId);

        dispatch(setLoadingState({ loadingConsent: true }));

        service
            .getLtdConsent()
            .then((response) => {
                const { consentStatus, redirectToAmway, redirectionPlace } =
                    response.data;
                const updatedConsent: ConsentFormDexie = {
                    id: config.user.memberId,
                    ltdConsentStatus: consentStatus,
                    redirectToAmwayConsentForm: redirectToAmway,
                    ssoRedirectionPlace: redirectionPlace,
                };

                setConsentStatusById(updatedConsent);
                dispatch(
                    setRedirectionState({
                        showRedirectionDialog: redirectToAmway,
                    }),
                );
            })
            .catch((error) => {
                dispatch(resetRedirectionState());
                captureSentryError(error, {
                    location: "ConsentFormApi",
                });
            })
            .finally(() =>
                dispatch(setLoadingState({ loadingConsent: false })),
            );

        setIsLtdConsentFormOpened("false");
        navigate(-1);
    };

    useEffect(() => {
        if (success) dismiss();
    }, [success]);

    return {
        header,
        subHeader,
        text,
        loading: textLoading || postLoading,
        success,
        consentGiven,
        dismiss,
    };
};
